(() => {
  const roots = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wideScreen = window.matchMedia('(min-width: 900px)');
  const compactLandscape = window.matchMedia(
    '(max-width: 899px) and (max-height: 600px) and (orientation: landscape)'
  );

  const initialize = (root) => {
    if (!root || roots.has(root)) return;

    const cleanups = [];
    const emit = (name, detail = {}) => {
      root.dispatchEvent(
        new CustomEvent('creative-works:' + name, {
          bubbles: true,
          detail
        })
      );
    };
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const networkIsConstrained = () => Boolean(
      connection && (
        connection.saveData ||
        connection.effectiveType === 'slow-2g' ||
        connection.effectiveType === '2g' ||
        connection.effectiveType === '3g'
      )
    );
    const ambientIsAllowed = () => !reducedMotion.matches && !networkIsConstrained();
    const revealItems = Array.from(root.querySelectorAll('[data-cw-reveal]'));

    root.dataset.cwReady = 'true';
    root.dataset.cwMotion = reducedMotion.matches ? 'reduced' : 'ready';
    root.dataset.cwMedia = ambientIsAllowed() ? 'ambient' : 'poster-only';

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -5% 0px', threshold: 0.08 }
      );

      revealItems.forEach((item) => revealObserver.observe(item));
      cleanups.push(() => revealObserver.disconnect());
    }

    const chapters = Array.from(root.querySelectorAll('[data-cw-chapter]'));
    const indexLinks = Array.from(root.querySelectorAll('[data-cw-index-link]'));
    const viewedChapters = new Set();
    let activeChapterNumber = '';

    const setActiveChapter = (chapter) => {
      if (!chapter) return;
      const number = chapter.dataset.cwChapterNumber;
      if (!number || number === activeChapterNumber) return;
      activeChapterNumber = number;
      root.dataset.cwActiveChapter = number;

      indexLinks.forEach((link) => {
        if (link.dataset.cwIndexNumber === number) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (!viewedChapters.has(number)) {
        viewedChapters.add(number);
        emit('chapter-view', { chapter: number });
      }
    };

    if (chapters.length) {
      setActiveChapter(chapters[0]);
    }

    if ('IntersectionObserver' in window && chapters.length) {
      const chapterObserver = new IntersectionObserver(
        (entries) => {
          const activeEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (activeEntry) setActiveChapter(activeEntry.target);
        },
        { rootMargin: '-35% 0px -63% 0px', threshold: 0 }
      );
      chapters.forEach((chapter) => chapterObserver.observe(chapter));
      cleanups.push(() => chapterObserver.disconnect());
    }

    indexLinks.forEach((link) => {
      const onIndexClick = (event) => {
        const target = root.querySelector(link.hash);
        if (!target) return;
        event.preventDefault();
        setActiveChapter(target);
        target.scrollIntoView({
          behavior: reducedMotion.matches ? 'instant' : 'smooth',
          block: 'start'
        });
        const targetHeading = target.querySelector('.cw-chapter__title');
        if (targetHeading) targetHeading.focus({ preventScroll: true });
        if (history.pushState && window.location.hash !== link.hash) {
          history.pushState(null, '', link.hash);
        }
        emit('chapter-select', { chapter: link.dataset.cwIndexNumber });
      };
      link.addEventListener('click', onIndexClick);
      cleanups.push(() => link.removeEventListener('click', onIndexClick));
    });

    root.querySelectorAll('[data-cw-product-link]').forEach((link) => {
      const onProductClick = () => {
        emit('fragrance-select', { product: link.dataset.cwProduct });
      };
      link.addEventListener('click', onProductClick);
      cleanups.push(() => link.removeEventListener('click', onProductClick));
    });

    const videos = Array.from(root.querySelectorAll('[data-cw-video]'));
    const activeVideos = new Set();
    let videoObserver;

    const sourceFor = (video) => {
      const desktopSource = video.dataset.cwVideoDesktop;
      return (wideScreen.matches || compactLandscape.matches)
        ? desktopSource
        : (video.dataset.cwVideoMobile || desktopSource);
    };

    const toggleFor = (video) => video.closest('.cw-chapter__scene')?.querySelector('[data-cw-video-toggle]');

    const setToggleState = (video, paused) => {
      const toggle = toggleFor(video);
      if (!toggle) return;
      toggle.dataset.cwPaused = paused ? 'true' : 'false';
      toggle.setAttribute(
        'aria-label',
        paused ? toggle.dataset.cwLabelPlay : toggle.dataset.cwLabelPause
      );
    };

    const hydrateVideo = (video) => {
      if (!ambientIsAllowed()) return false;
      if (video.closest('.cw-chapter__scene')?.hasAttribute('data-cw-video-error')) return false;
      const source = sourceFor(video);
      if (!source) return false;
      if (video.dataset.cwVideoSource === source) return true;

      video.pause();
      video.removeAttribute('data-cw-video-ready');
      video.src = source;
      video.dataset.cwVideoSource = source;
      video.load();
      return true;
    };

    const playVideo = (video) => {
      if (
        video.dataset.cwUserPaused === 'true' ||
        document.hidden ||
        document.documentElement.hasAttribute('data-cw-film-open') ||
        !hydrateVideo(video)
      ) {
        setToggleState(video, true);
        return;
      }

      const playback = video.play();
      if (playback && typeof playback.then === 'function') {
        playback
          .then(() => setToggleState(video, false))
          .catch(() => setToggleState(video, true));
      }
    };

    const pauseVideo = (video) => {
      video.pause();
      setToggleState(video, true);
    };

    videos.forEach((video) => {
      setToggleState(video, true);

      const onVideoReady = () => video.setAttribute('data-cw-video-ready', '');
      const onVideoError = () => {
        const scene = video.closest('.cw-chapter__scene');
        if (scene?.hasAttribute('data-cw-video-error')) return;
        scene?.setAttribute('data-cw-video-error', '');
        pauseVideo(video);
        video.removeAttribute('data-cw-video-ready');
        video.removeAttribute('src');
        delete video.dataset.cwVideoSource;
        video.load();
      };
      video.addEventListener('loadeddata', onVideoReady);
      video.addEventListener('playing', onVideoReady);
      video.addEventListener('error', onVideoError);
      cleanups.push(() => video.removeEventListener('loadeddata', onVideoReady));
      cleanups.push(() => video.removeEventListener('playing', onVideoReady));
      cleanups.push(() => video.removeEventListener('error', onVideoError));

      const toggle = toggleFor(video);
      if (!toggle) return;
      const onToggle = () => {
        if (video.paused || video.dataset.cwUserPaused === 'true') {
          delete video.dataset.cwUserPaused;
          playVideo(video);
          emit('ambient-play', { chapter: video.closest('[data-cw-chapter]')?.dataset.cwChapterNumber });
        } else {
          video.dataset.cwUserPaused = 'true';
          pauseVideo(video);
          emit('ambient-pause', { chapter: video.closest('[data-cw-chapter]')?.dataset.cwChapterNumber });
        }
      };
      toggle.addEventListener('click', onToggle);
      cleanups.push(() => toggle.removeEventListener('click', onToggle));
    });

    if ('IntersectionObserver' in window && videos.length) {
      videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              activeVideos.add(entry.target);
              playVideo(entry.target);
            } else {
              activeVideos.delete(entry.target);
              entry.target.pause();
            }
          });
        },
        { rootMargin: '160px 0px', threshold: 0.12 }
      );
      videos.forEach((video) => videoObserver.observe(video));
      cleanups.push(() => videoObserver.disconnect());
    } else if (videos.length && ambientIsAllowed()) {
      videos.forEach((video) => {
        activeVideos.add(video);
        playVideo(video);
      });
    }

    const refreshAmbientMedia = () => {
      root.dataset.cwMotion = reducedMotion.matches ? 'reduced' : 'ready';
      root.dataset.cwMedia = ambientIsAllowed() ? 'ambient' : 'poster-only';
      if (reducedMotion.matches) revealItems.forEach((item) => item.classList.add('is-visible'));
      videos.forEach((video) => {
        if (!ambientIsAllowed()) {
          pauseVideo(video);
          video.removeAttribute('data-cw-video-ready');
          video.removeAttribute('src');
          delete video.dataset.cwVideoSource;
          video.load();
          return;
        }
        if (activeVideos.has(video)) playVideo(video);
      });
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        videos.forEach((video) => video.pause());
      } else {
        activeVideos.forEach(playVideo);
      }
    };

    wideScreen.addEventListener('change', refreshAmbientMedia);
    compactLandscape.addEventListener('change', refreshAmbientMedia);
    reducedMotion.addEventListener('change', refreshAmbientMedia);
    document.addEventListener('visibilitychange', onVisibilityChange);
    cleanups.push(() => wideScreen.removeEventListener('change', refreshAmbientMedia));
    cleanups.push(() => compactLandscape.removeEventListener('change', refreshAmbientMedia));
    cleanups.push(() => reducedMotion.removeEventListener('change', refreshAmbientMedia));
    cleanups.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));

    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', refreshAmbientMedia);
      cleanups.push(() => connection.removeEventListener('change', refreshAmbientMedia));
    }

    const dialog = root.querySelector('[data-cw-film-dialog]');
    const player = dialog?.querySelector('[data-cw-film-player]');
    const dialogTitle = dialog?.querySelector('[data-cw-film-title]');
    const dialogDescription = dialog?.querySelector('[data-cw-film-description]');
    const filmError = dialog?.querySelector('[data-cw-film-error]');
    const filmRetry = dialog?.querySelector('[data-cw-film-retry]');
    const filmTriggers = Array.from(root.querySelectorAll('[data-cw-film-open]'));
    let lastFilmTrigger;
    let currentFilmSource = '';

    const clearFilmTrack = () => {
      player?.querySelectorAll('[data-cw-film-track]').forEach((track) => track.remove());
    };

    const configureFilmTrack = (trigger) => {
      if (!player) return;
      clearFilmTrack();
      const captions = trigger.dataset.cwFilmCaptions;
      if (!captions) return;
      const track = document.createElement('track');
      track.kind = 'captions';
      track.src = captions;
      track.srclang = trigger.dataset.cwFilmCaptionsLanguage || document.documentElement.lang || 'en';
      track.label = trigger.dataset.cwFilmCaptionsLabel || track.srclang;
      track.default = true;
      track.setAttribute('data-cw-film-track', '');
      player.appendChild(track);
    };

    const clearFilmError = () => {
      if (!dialog || !filmError) return;
      dialog.removeAttribute('data-cw-film-failed');
      filmError.hidden = true;
    };

    const loadFilm = () => {
      if (!dialog || !player || !currentFilmSource) return;
      clearFilmError();
      dialog.setAttribute('aria-busy', 'true');
      player.src = currentFilmSource;
      player.load();
      const playback = player.play();
      if (playback && typeof playback.catch === 'function') playback.catch(() => {});
    };

    const clearFilm = () => {
      if (!player) return;
      player.pause();
      player.removeAttribute('src');
      player.load();
      currentFilmSource = '';
      player.removeAttribute('aria-label');
      clearFilmTrack();
      clearFilmError();
      dialog?.removeAttribute('aria-busy');
      if (dialogDescription) dialogDescription.textContent = '';
      document.documentElement.removeAttribute('data-cw-film-open');
      activeVideos.forEach(playVideo);
      if (lastFilmTrigger?.isConnected) lastFilmTrigger.focus({ preventScroll: true });
      lastFilmTrigger = null;
    };

    const closeFilm = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        clearFilm();
      }
    };

    filmTriggers.forEach((trigger) => {
      const onFilmOpen = (event) => {
        if (!dialog || !player || !dialogTitle) return;
        const source = wideScreen.matches
          ? trigger.dataset.cwFilmDesktop
          : (trigger.dataset.cwFilmMobile || trigger.dataset.cwFilmDesktop);
        if (!source) return;

        event.preventDefault();
        lastFilmTrigger = trigger;
        currentFilmSource = source;
        videos.forEach((video) => video.pause());
        dialogTitle.textContent = trigger.dataset.cwFilmTitle || '';
        player.setAttribute(
          'aria-label',
          trigger.dataset.cwFilmLabel || trigger.dataset.cwFilmTitle || ''
        );
        configureFilmTrack(trigger);
        if (dialogDescription) {
          dialogDescription.textContent = trigger.dataset.cwFilmDescription || '';
        }
        document.documentElement.setAttribute('data-cw-film-open', '');

        if (typeof dialog.showModal === 'function') {
          if (!dialog.open) dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }

        loadFilm();
        emit('film-open', { product: trigger.dataset.cwProduct });
      };
      trigger.addEventListener('click', onFilmOpen);
      cleanups.push(() => trigger.removeEventListener('click', onFilmOpen));
    });

    if (dialog && player) {
      const onDialogClose = () => clearFilm();
      const onDialogClick = (event) => {
        if (event.target === dialog) closeFilm();
      };
      const onFilmEnded = () => {
        emit('film-complete', { title: dialogTitle?.textContent || '' });
      };
      const onFilmReady = () => {
        dialog.removeAttribute('aria-busy');
        clearFilmError();
      };
      const onFilmError = () => {
        if (!dialog.open || !currentFilmSource || !filmError) return;
        dialog.removeAttribute('aria-busy');
        dialog.setAttribute('data-cw-film-failed', '');
        filmError.hidden = false;
      };
      const onFilmRetry = () => loadFilm();
      dialog.addEventListener('close', onDialogClose);
      dialog.addEventListener('click', onDialogClick);
      player.addEventListener('loadeddata', onFilmReady);
      player.addEventListener('playing', onFilmReady);
      player.addEventListener('error', onFilmError);
      player.addEventListener('ended', onFilmEnded);
      filmRetry?.addEventListener('click', onFilmRetry);
      cleanups.push(() => dialog.removeEventListener('close', onDialogClose));
      cleanups.push(() => dialog.removeEventListener('click', onDialogClick));
      cleanups.push(() => player.removeEventListener('loadeddata', onFilmReady));
      cleanups.push(() => player.removeEventListener('playing', onFilmReady));
      cleanups.push(() => player.removeEventListener('error', onFilmError));
      cleanups.push(() => player.removeEventListener('ended', onFilmEnded));
      cleanups.push(() => filmRetry?.removeEventListener('click', onFilmRetry));
      cleanups.push(() => {
        if (dialog.open) dialog.close();
        clearFilm();
      });
    }

    const parallaxItems = Array.from(root.querySelectorAll('[data-cw-parallax]'));
    const heroImage = root.querySelector('.cw-hero__media [data-cw-parallax-image]');
    const hero = root.querySelector('.cw-hero');
    let frame = 0;
    let parallaxBound = false;

    const updateParallax = () => {
      frame = 0;
      if (reducedMotion.matches || !wideScreen.matches) return;

      const viewportHeight = window.innerHeight || 1;
      const shifts = parallaxItems.map((item) => {
        const rect = item.getBoundingClientRect();
        const progress = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
        return Math.max(-16, Math.min(16, progress * -18)).toFixed(2) + 'px';
      });
      let heroShift;
      if (heroImage && hero) {
        const heroRect = hero.getBoundingClientRect();
        const heroProgress = Math.max(0, Math.min(1, -heroRect.top / Math.max(heroRect.height, 1)));
        heroShift = (heroProgress * 16).toFixed(2) + 'px';
      }

      parallaxItems.forEach((item, index) => item.style.setProperty('--cw-shift', shifts[index]));
      if (heroImage && heroShift) heroImage.style.setProperty('--cw-parallax', heroShift);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateParallax);
    };

    const clearParallax = () => {
      parallaxItems.forEach((item) => item.style.removeProperty('--cw-shift'));
      if (heroImage) heroImage.style.removeProperty('--cw-parallax');
    };

    const syncParallaxBinding = () => {
      const shouldBind = wideScreen.matches && !reducedMotion.matches && (parallaxItems.length || heroImage);
      if (shouldBind && !parallaxBound) {
        parallaxBound = true;
        updateParallax();
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
      } else if (!shouldBind && parallaxBound) {
        parallaxBound = false;
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
        if (frame) window.cancelAnimationFrame(frame);
        frame = 0;
        clearParallax();
      }
    };

    syncParallaxBinding();
    wideScreen.addEventListener('change', syncParallaxBinding);
    reducedMotion.addEventListener('change', syncParallaxBinding);
    cleanups.push(() => {
      wideScreen.removeEventListener('change', syncParallaxBinding);
      reducedMotion.removeEventListener('change', syncParallaxBinding);
      if (parallaxBound) {
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);
      }
      if (frame) window.cancelAnimationFrame(frame);
      clearParallax();
    });

    cleanups.push(() => {
      root.removeAttribute('data-cw-ready');
      root.removeAttribute('data-cw-active-chapter');
      videos.forEach((video) => video.pause());
    });
    roots.set(root, () => cleanups.forEach((cleanup) => cleanup()));
  };

  const destroy = (root) => {
    const cleanup = roots.get(root);
    if (!cleanup) return;
    cleanup();
    roots.delete(root);
  };

  const initializeAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-cw-root]')) initialize(scope);
    scope.querySelectorAll('[data-cw-root]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches('[data-cw-root]')
      ? event.target
      : event.target.querySelector('[data-cw-root]');
    destroy(root);
  });
})();
